import { chunkArray } from "app/containers/Features/containers/Boards/helpers";
import { IPagination } from "app/types";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { TICKET_API_URL } from "utils/constants";
import { handleApiError } from "utils/errorHandler";
import { request } from "utils/request";
import {
  IDashboardMyProject,
  IDashboardUserTicket,
  IProjectDetails,
  IUserActivity,
} from "../type";
import {
  selectActivitiesPagination,
  selectActivityFilters,
  selectMyProjects,
  selectMyTicketsFilters,
  selectProjectDetails,
  selectTicketsPagination,
} from "./selector";
import { actions } from "./slice";

const fetchProjectDetailsHelper = function* (projectIds: string[]) {
  if (!projectIds.length) return [];
  try {
    const existingDetails: IProjectDetails[] =
      yield select(selectProjectDetails);
    const existingIds = new Set(
      existingDetails.map((projectDetail) => projectDetail._id),
    );
    const missingIds = projectIds.filter(
      (id) => Boolean(id) && !existingIds.has(id),
    );

    if (!missingIds.length) return [];

    const chunks = chunkArray(missingIds, 3);
    let allData: IProjectDetails[] = [];

    for (const chunk of chunks) {
      if (!chunk || Object.keys(chunk).length === 0) continue;
      const url = `${TICKET_API_URL}dashboard/projects/details?projectIds=${chunk.join(
        ",",
      )}`;
      const options = { method: "GET" };
      const { data } = yield call(request, url, options);
      if (data?.data) {
        allData = [...allData, ...((data.data as IProjectDetails[]) || [])];
      }
    }
    return allData;
  } catch (e) {
    return [];
  }
};

export function* getChartDataRequest() {
  try {
    delay(500);
    yield put(actions.setChartLoading(true));
    const url = `${TICKET_API_URL}dashboard/chart`;
    const options = { method: "GET" };

    const { data, err } = yield call(request, url, options);
    yield put(actions.setChartLoading(false));

    if (err || !data) {
      handleApiError(err);
      return;
    }
    const { myTickets = [], myProjects = [] } = data.data || {};

    yield put(actions.setMyTickets(myTickets));
    yield put(actions.setMyProjects(myProjects));

    yield put(actions.getUserActivities());
    yield put(actions.getMyTickets());

    const projectIds = (myProjects as IDashboardMyProject[])
      .map((project) => project._id)
      .filter(Boolean);
    if (projectIds.length > 0) {
      const details = yield call(fetchProjectDetailsHelper, projectIds);
      yield put(actions.setProjectDetails(details));
    }
  } catch (err) {
    yield put(actions.setChartLoading(false));
    handleApiError(err);
  }
}

export function* getMyTicketsRequest() {
  try {
    const pagination: IPagination = yield select(selectTicketsPagination);
    if (pagination.page === 1) {
      yield delay(500);
    }
    const filters = yield select(selectMyTicketsFilters);
    const myProjects: IDashboardMyProject[] = yield select(selectMyProjects);
    const { projectIds, statusIds, dueDate } = filters;

    const reqProjectIds =
      projectIds.length > 0
        ? projectIds
        : myProjects.map((project) => project._id);

    const generatedChunks = chunkArray(reqProjectIds, 10);
    const chunks = generatedChunks.length > 0 ? generatedChunks : [[]];

    let allData: IDashboardUserTicket[] = [];
    let meta: IPagination | undefined;

    const requestParams = `page=${pagination.page}&perPage=${pagination.perPage}`;
    const baseOptionalParams = `${
      pagination.orderBy ? "&orderBy=" + pagination.orderBy : ""
    }${
      pagination.orderDirection
        ? "&orderDirection=" + pagination.orderDirection
        : ""
    }${statusIds.length > 0 ? "&statusIds=" + statusIds : ""}${
      dueDate.length > 0
        ? "&startDate=" + dueDate[0] + "&endDate=" + (dueDate[1] || dueDate[0])
        : ""
    }`;

    for (const chunk of chunks) {
      const optionalRequestParams = `${baseOptionalParams}${
        chunk?.length > 0 ? "&projectIds=" + chunk.join(",") : ""
      }`;

      const url = `${TICKET_API_URL}dashboard/my-tickets?${requestParams + optionalRequestParams}`;
      const options = { method: "GET" };

      const { data, err } = yield call(request, url, options);
      if (!err && data) {
        allData = [
          ...allData,
          ...((data.data as IDashboardUserTicket[]) || []),
        ];
        if (!meta) {
          meta = data._metadata?.pagination as IPagination;
        }
      }
    }

    yield put(actions.setTicketsLoading(false));

    if (!meta) {
      const hasMore = allData.length >= pagination.perPage;
      meta = {
        ...pagination,
        totalPage: hasMore ? pagination.page + 1 : pagination.page,
        total: allData.length,
      };
    }

    yield put(actions.setMyTicketsList(allData));
    yield put(actions.setTicketsPagination(meta));

    const uniqueProjectIds = Array.from(
      new Set(allData.map((ticket) => ticket.projectId).filter(Boolean)),
    ) as string[];

    if (uniqueProjectIds.length > 0) {
      const details = yield call(fetchProjectDetailsHelper, uniqueProjectIds);
      yield put(actions.setProjectDetails(details));
    }
  } catch (err) {
    yield put(actions.setTicketsLoading(false));
    handleApiError(err);
  }
}

export function* getProjectDetailsRequest(action: {
  payload: { projectIds: string[] };
}) {
  try {
    const { projectIds } = action.payload;
    if (!projectIds.length) return;
    const details = yield call(fetchProjectDetailsHelper, projectIds);
    yield put(actions.setProjectDetails(details));
  } catch (err) {
    handleApiError(err);
  }
}

export function* getUserActivitiesRequest() {
  try {
    const pagination: IPagination = yield select(selectActivitiesPagination);
    if (pagination.page === 1) {
      yield delay(500);
    }
    
    const filters = yield select(selectActivityFilters);
    const requestParams = `page=${pagination.page}&perPage=${pagination.perPage}`;

    const myProjects: IDashboardMyProject[] = yield select(selectMyProjects);

    const reqProjectIds =
      filters.projectIds.length > 0
        ? filters.projectIds
        : myProjects.map((project) => project._id);

    const generatedChunks = chunkArray(reqProjectIds, 10);
    const chunks = generatedChunks.length > 0 ? generatedChunks : [[]];

    let allData: IUserActivity[] = [];
    let meta: IPagination | undefined;

    const baseOptionalParams = `${
      pagination.orderBy ? "&orderBy=" + pagination.orderBy : ""
    }${
      pagination.orderDirection
        ? "&orderDirection=" + pagination.orderDirection
        : ""
    }`;

    for (const chunk of chunks) {
      const optionalRequestParams = `${baseOptionalParams}${
        chunk?.length > 0 ? "&projectIds=" + chunk.join(",") : ""
      }`;
      const url = `${TICKET_API_URL}dashboard/user-activities?${requestParams + optionalRequestParams}`;
      const options = { method: "GET" };

      const { data, err } = yield call(request, url, options);
      if (!err && data) {
        allData = [...allData, ...((data.data as IUserActivity[]) || [])];
        if (!meta) {
          meta = data._metadata?.pagination as IPagination;
        }
      }
    }

    if (!meta) {
      const hasMore = allData.length >= pagination.perPage;
      meta = {
        ...pagination,
        totalPage: hasMore ? pagination.page + 1 : pagination.page,
        total: allData.length,
      };
    }

    yield put(actions.setUserActivities(allData));
    yield put(actions.setActivitiesPagination(meta));
  yield put(actions.setActivitiesLoading(false));
    const projectIds = Array.from(
      new Set(
        allData
          .map((activity: IUserActivity) => {
            const projectData = activity.project as string | { _id?: string };
            return typeof projectData === "object" && projectData !== null
              ? projectData._id
              : projectData;
          })
          .filter((projectId) => typeof projectId === "string"),
      ),
    ) as string[];

    if (projectIds.length > 0) {
      const details = yield call(fetchProjectDetailsHelper, projectIds);
      yield put(actions.setProjectDetails(details));
    }
  
  } catch (err) {
    yield put(actions.setActivitiesLoading(false));
    handleApiError(err);
  }
}

export default function* homeSaga() {
  yield takeLatest(actions.getChartData, getChartDataRequest);
  yield takeLatest(actions.getMyTickets, getMyTicketsRequest);
  yield takeLatest(actions.getProjectDetails, getProjectDetailsRequest);
  yield takeLatest(actions.getUserActivities, getUserActivitiesRequest);
}
